import { ExternalLinkIcon, RefreshIcon } from "@heroicons/react/outline";
import { useState } from "react";

import { SectionShell } from "./SectionShell";

import { Badge, BadgeVariant } from "@/components/ui/Badge";
import { Button } from "@/components/ui/button";
import { CopyButton } from "@/components/ui/copy-button";
import { useGenerateAdminPortalLink } from "@/queries/identity/useAdminPortalLink";
import { IdentitySettings } from "@/queries/identity/useIdentitySettings";
import { useSyncWorkos } from "@/queries/identity/useSyncWorkos";
import { useTestSso } from "@/queries/identity/useTestSso";
import { ToastType } from "@/enums";
import { displayToast } from "@/utils/toast";

interface Props {
  organizationId: number;
  settings: IdentitySettings;
  onError: (msg: string | null) => void;
}

const formatStatusLabel = (value: string | null | undefined) => {
  if (!value) {
    return "Not configured";
  }
  return value.replace(/_/g, " ");
};

const resolveConnectionBadge = (
  resourceId: string | null | undefined,
  status: string | null | undefined,
) => {
  if (!resourceId) {
    return { label: "Not configured", variant: BadgeVariant.GRAY };
  }
  if (status === "active") {
    return { label: "Active", variant: BadgeVariant.GREEN };
  }
  return { label: formatStatusLabel(status), variant: BadgeVariant.ORANGE };
};

/**
 * Connection setup. Hosts the WorkOS Admin Portal buttons (SSO and
 * DSync), read-only synced WorkOS ids, and the Test SSO action.
 */
export const ConnectionSection = ({ organizationId, settings, onError }: Props) => {
  const generateLink = useGenerateAdminPortalLink(organizationId);
  const syncWorkos = useSyncWorkos(organizationId);
  const testSso = useTestSso(organizationId);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const isOidc = settings.protocol === "oidc";
  const ssoStatus = resolveConnectionBadge(
    settings.workos_sso_connection_id,
    settings.sso_connection_status,
  );
  const directoryStatus = resolveConnectionBadge(
    settings.workos_directory_id,
    settings.directory_status,
  );

  const handleSync = () => {
    onError(null);
    setIsRefreshing(true);
    syncWorkos.mutate(undefined, {
      onSuccess: () => {
        displayToast("WorkOS connection details refreshed.", ToastType.success);
      },
      onError: () =>
        onError(
          "Could not refresh WorkOS connection details. Confirm WorkOS is configured and try again.",
        ),
      onSettled: () => setIsRefreshing(false),
    });
  };

  const openPortal = (intent: "sso" | "dsync") => {
    onError(null);
    generateLink.mutate(
      { intent },
      {
        onSuccess: ({ link }) => {
          if (link) {
            window.open(link, "_blank", "noopener,noreferrer");
          }
        },
        onError: () => onError("Could not generate WorkOS Admin Portal link. Try again."),
      },
    );
  };

  const handleTestSso = () => {
    if (!settings.workos_sso_connection_id) {
      onError("Complete the Admin Portal SSO flow first, then refresh from WorkOS.");
      return;
    }
    onError(null);
    testSso.mutate(
      {},
      {
        onSuccess: ({ authorization_url }) => {
          if (authorization_url) {
            window.open(authorization_url, "_blank", "noopener,noreferrer");
            displayToast(
              "Test SSO launched. Check the new tab for the round-trip result.",
              ToastType.success,
            );
          }
        },
        onError: () =>
          onError(
            "Could not initiate Test SSO. Confirm the SSO connection is active in WorkOS and try again.",
          ),
      },
    );
  };

  return (
    <SectionShell
      title="Connection setup"
      description="Configure SSO and Directory Sync via the WorkOS Admin Portal — the primary onboarding path for your IT administrators."
      actions={
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handleSync} disabled={isRefreshing} className="gap-2">
            <RefreshIcon className="h-4 w-4" />
            {isRefreshing ? "Refreshing…" : "Refresh from WorkOS"}
          </Button>
          <Button
            variant="outline"
            onClick={handleTestSso}
            disabled={!settings.workos_sso_connection_id || testSso.isLoading}
            className="gap-2"
          >
            <RefreshIcon className="h-4 w-4" /> {testSso.isLoading ? "Launching…" : "Test SSO"}
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Button
          variant="default"
          onClick={() => openPortal("sso")}
          disabled={generateLink.isLoading}
          className="gap-2"
        >
          <ExternalLinkIcon className="h-4 w-4" /> Open SSO Admin Portal
        </Button>
        <Button
          variant="default"
          onClick={() => openPortal("dsync")}
          disabled={generateLink.isLoading}
          className="gap-2"
        >
          <ExternalLinkIcon className="h-4 w-4" /> Open Directory Sync Admin Portal
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-gray-600">SSO</span>
          <Badge variant={ssoStatus.variant} className="capitalize">
            {ssoStatus.label}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-gray-600">Directory sync</span>
          <Badge variant={directoryStatus.variant} className="capitalize">
            {directoryStatus.label}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-gray-600">Protocol</span>
          <span className="font-medium text-gray-900">{isOidc ? "OIDC" : "SAML"}</span>
        </div>
        <p className="w-full text-xs text-gray-500">
          Status refreshes from WorkOS after Admin Portal setup. Use Refresh from WorkOS if you just
          finished configuring your IdP.
        </p>
      </div>

      {settings.service_provider_details &&
        settings.service_provider_details.length > 0 &&
        settings.workos_sso_connection_id && (
          <div className="space-y-2 rounded border border-dashed border-gray-300 bg-white p-3 text-xs">
            <div className="font-medium text-gray-700">
              Service provider details ({isOidc ? "OIDC" : "SAML"})
            </div>
            <p className="text-[11px] text-gray-500">
              Paste these into your identity provider&apos;s application configuration. WorkOS Admin
              Portal does not show these values after the connection is active.
            </p>
            {settings.service_provider_details.map(({ label, value }) => (
              <div key={label} className="flex items-center justify-between gap-2">
                <span className="shrink-0 text-gray-500">{label}</span>
                <div className="flex min-w-0 items-center gap-1">
                  <code className="truncate font-mono text-[11px] text-gray-700">{value}</code>
                  {value !== "—" && <CopyButton text={value} iconSize="h-3.5 w-3.5" />}
                </div>
              </div>
            ))}
          </div>
        )}
    </SectionShell>
  );
};
